import { type IntlError, IntlErrorCode } from 'next-intl';

import { type Locale } from '@/modules/language/constants';
import { getLogger } from '@/modules/observability/logging';

export function handleNextIntlError(error: IntlError) {
  if (process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'production') {
    console.error(
      `Intl error - see https://depopmarket.atlassian.net/wiki/spaces/WD/pages/4936171553/How+to+Fix+next-intl+Errors`,
      { error }
    );
  }
  // next-intl calls both `onError` and `getMessageFallback` for every failure.
  // `getMessageFallback` receives the failing `key` / `namespace`, so it owns the
  // error-level logging for non-`MISSING_MESSAGE` codes (INVALID_MESSAGE etc.).
  // We deliberately don't re-log those here: emitting an error from both callbacks
  // doubled the volume during the burst this PR responds to (99.5% INVALID_MESSAGE).
  //
  // `MISSING_MESSAGE` is handled here instead, at info level, because a missing
  // translation is expected fallback behaviour rather than an error. Unlike
  // INVALID_MESSAGE (whose `originalMessage` is `void 0` in the production bundle,
  // making `error.message` the bare code), a MISSING_MESSAGE IntlError is built
  // with a descriptive `originalMessage`, so `error.message` reads e.g.
  // "MISSING_MESSAGE: Namespace.key (en)" - the key and locale in one string. We
  // therefore keep `message` (the human-readable clue) plus `code`/`stack` for the
  // server logger, and pass the `error` as the third arg so the browser (Datadog)
  // logger can attach a proper error object; the server logger ignores it.
  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    getLogger().info(
      `Intl error - missing translation`,
      {
        code: error.code,
        message: error.message,
        stack: error.stack,
      },
      error
    );
  }
}

export function getMessageFallback({
  error,
  key,
  namespace,
}: {
  error: IntlError;
  key: string;
  namespace?: string | undefined;
}) {
  // next-intl calls `getMessageFallback` alongside `onError` whenever a message
  // fails, but unlike `onError` it receives the `key` and `namespace` of the
  // offending translation. Since `onError` can't recover the failing string for an
  // INVALID_MESSAGE in production, this is the single place we log those (at error
  // level) - `onError` stays silent for non-missing codes so a burst emits one
  // error per failure, not two. MISSING_MESSAGE is skipped here because `onError`
  // already reports it at info level. Locale isn't passed to this callback, so we
  // read the provider's default locale from the same env var `LanguageProvider`
  // uses. The `error` is passed as the third logger arg so the browser (Datadog)
  // logger attaches a proper error object for grouping/triage; the server logger
  // ignores it and keeps the structured `code`/`key`/`namespace` context.
  if (error.code !== IntlErrorCode.MISSING_MESSAGE) {
    getLogger().error(
      `Intl error - failed to resolve message`,
      {
        code: error.code,
        key,
        namespace,
        locale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
      },
      error
    );
  }
  return key;
}

/*
This function imports a message file based on the locale and file name.
If the message file does not exist and the locale is the default locale, it throws an error because otherwise
we will not have any strings for that feature. Arguably if someone is working on a feature and does this, they will
crash in dev and fix the problem. We should never see this get to production.
Meanwhile, if the message file does not exist and the locale is not the default locale, it logs an error and returns an empty object.
This will prevent us having an incident because someone forgot to create message files for each locale. At the same time,
the errors will trigger an alert so we will know to fix the issue.
*/
export async function importMessageFile(locale: Locale, fileName: string) {
  try {
    const file = await import(`@/messages/${locale}/${fileName}.json`);
    return file.default;
  } catch (error) {
    const { NEXT_PUBLIC_DEFAULT_LOCALE } = process.env;
    if (locale === NEXT_PUBLIC_DEFAULT_LOCALE) {
      throw error;
    }
    console.error(
      `Error importing message file ${fileName} for locale ${locale}:`,
      error
    );
    return {};
  }
}
